import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  Info,
  ArrowDownAZ,
  ArrowUpAZ,
  CheckCircle2,
  Eye,
  FileCode2,
  FileText,
  LinkIcon,
  Receipt,
  Search,
  Trash2,
  Upload,
  WalletCards
} from 'lucide-react';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
});

const dateFormatter = new Intl.DateTimeFormat('pt-BR');

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const safeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const ImportedNfseWorkspace = ({
  isUploadingNfse,
  handleUploadNfse,
  importedNfses,
  invoices = [],
  onOpenLink,
  onOpenViewer,
  handleDeleteImportedNfse
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [linkFilter, setLinkFilter] = useState('all');
  const [fileFilter, setFileFilter] = useState('all');
  const [sortMode, setSortMode] = useState('emission_desc');

  const invoiceById = useMemo(
    () => Object.fromEntries((Array.isArray(invoices) ? invoices : []).map((invoice) => [invoice.id, invoice])),
    [invoices]
  );

  const notes = Array.isArray(importedNfses) ? importedNfses : [];

  const summary = useMemo(() => {
    const linked = notes.filter((item) => Boolean(item.invoice_id));
    const pending = notes.filter((item) => !item.invoice_id);
    return {
      total: notes.length,
      linked: linked.length,
      pending: pending.length,
      totalValue: notes.reduce((sum, item) => sum + Number(item.valor_servico || 0), 0),
      pendingValue: pending.reduce((sum, item) => sum + Number(item.valor_servico || 0), 0)
    };
  }, [notes]);

  const filteredNotes = useMemo(() => {
    const query = normalizeText(searchTerm.trim());

    return [...notes]
      .filter((item) => {
        if (linkFilter === 'linked' && !item.invoice_id) return false;
        if (linkFilter === 'unlinked' && item.invoice_id) return false;
        if (fileFilter === 'xml' && !item.xml_content) return false;
        if (fileFilter === 'pdf' && !item.pdf_base64) return false;

        if (!query) return true;
        const linkedInvoice = invoiceById[item.invoice_id];
        return normalizeText([
          item.numero_nfse,
          item.chave_acesso,
          item.tomador_nome,
          item.tomador_cpf_cnpj,
          item.prestador_nome,
          item.prestador_cnpj,
          item.filename,
          item.original_filename,
          linkedInvoice?.invoice_number,
          linkedInvoice?.patient_name
        ].filter(Boolean).join(' ')).includes(query);
      })
      .sort((a, b) => {
        if (sortMode === 'value_desc') return Number(b.valor_servico || 0) - Number(a.valor_servico || 0);
        if (sortMode === 'value_asc') return Number(a.valor_servico || 0) - Number(b.valor_servico || 0);
        if (sortMode === 'number_asc') return String(a.numero_nfse || '').localeCompare(String(b.numero_nfse || ''), 'pt-BR', { numeric: true });
        if (sortMode === 'number_desc') return String(b.numero_nfse || '').localeCompare(String(a.numero_nfse || ''), 'pt-BR', { numeric: true });
        const dateA = safeDate(a.data_emissao)?.getTime() || 0;
        const dateB = safeDate(b.data_emissao)?.getTime() || 0;
        return sortMode === 'emission_asc' ? dateA - dateB : dateB - dateA;
      });
  }, [notes, searchTerm, linkFilter, fileFilter, sortMode, invoiceById]);

  const clearFilters = () => {
    setSearchTerm('');
    setLinkFilter('all');
    setFileFilter('all');
    setSortMode('emission_desc');
  };

  const renderStatus = (item) => {
    if (!item.invoice_id) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
          <AlertCircle className="h-3.5 w-3.5" /> Aguardando vínculo
        </span>
      );
    }
    const invoice = invoiceById[item.invoice_id];
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
          <CheckCircle2 className="h-3.5 w-3.5" /> Vinculada
        </span>
        <div className="text-xs text-gray-500">{invoice ? `Fatura #${invoice.invoice_number}` : 'Fatura vinculada'}</div>
      </div>
    );
  };

  const openLink = (item) => onOpenLink?.(item);
  const openViewer = (item) => onOpenViewer?.(item);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-blue-50 p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">NFS-e importadas</h3>
            <p className="mt-1 max-w-3xl text-sm text-gray-600">
              Armazene notas já emitidas, confira seus dados e vincule cada documento à fatura correspondente.
            </p>
            <div className="mt-4 flex max-w-4xl items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
              <div>
                <p className="font-semibold">Formatos atualmente reconhecidos</p>
                <p className="mt-1 leading-5">
                  PDF ou XML do Emissor Nacional, PDF da Prefeitura de João Pessoa e PDF da Prefeitura do Recife. Confira os dados após a importação. Para notas de outra prefeitura ou caso encontre qualquer divergência, entre em contato com o suporte para incluirmos ou ajustarmos esse modelo.
                </p>
              </div>
            </div>
          </div>
          <label className={`inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${isUploadingNfse ? 'cursor-not-allowed bg-gray-300 text-gray-600' : 'bg-purple-600 text-white hover:bg-purple-700'}`}>
            <Upload className="mr-2 h-4 w-4" />
            {isUploadingNfse ? 'Importando arquivos...' : 'Importar NFS-e'}
            <input
              type="file"
              className="hidden"
              accept=".xml,.pdf,application/xml,text/xml,application/pdf"
              multiple
              onChange={handleUploadNfse}
              disabled={isUploadingNfse}
            />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {[
          { label: 'Notas importadas', value: summary.total, helper: 'Documentos armazenados', icon: Receipt },
          { label: 'Vinculadas', value: summary.linked, helper: 'Associadas a faturas', icon: CheckCircle2 },
          { label: 'Aguardando vínculo', value: summary.pending, helper: 'Precisam de conferência', icon: LinkIcon },
          { label: 'Valor importado', value: currencyFormatter.format(summary.totalValue), helper: 'Soma das notas', icon: WalletCards },
          { label: 'Valor não vinculado', value: currencyFormatter.format(summary.pendingValue), helper: 'Ainda sem fatura', icon: AlertCircle }
        ].map(({ label, value, helper, icon: Icon }) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
                <p className="mt-2 text-xl font-semibold text-gray-900">{value}</p>
                <p className="mt-1 text-xs text-gray-500">{helper}</p>
              </div>
              <div className="rounded-lg bg-purple-50 p-2 text-purple-700"><Icon className="h-5 w-5" /></div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(260px,1fr)_180px_180px_210px_auto]">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar número, tomador, CPF/CNPJ, chave ou fatura"
              className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
            />
          </label>
          <select value={linkFilter} onChange={(event) => setLinkFilter(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm">
            <option value="all">Todos os vínculos</option>
            <option value="linked">Vinculadas</option>
            <option value="unlinked">Não vinculadas</option>
          </select>
          <select value={fileFilter} onChange={(event) => setFileFilter(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm">
            <option value="all">Todos os arquivos</option>
            <option value="xml">Com XML</option>
            <option value="pdf">Com PDF</option>
          </select>
          <select value={sortMode} onChange={(event) => setSortMode(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm">
            <option value="emission_desc">Emissão mais recente</option>
            <option value="emission_asc">Emissão mais antiga</option>
            <option value="value_desc">Maior valor</option>
            <option value="value_asc">Menor valor</option>
            <option value="number_asc">Número crescente</option>
            <option value="number_desc">Número decrescente</option>
          </select>
          <button onClick={clearFilters} className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Limpar</button>
        </div>
        <p className="mt-3 text-xs text-gray-500">{filteredNotes.length} de {notes.length} nota(s) exibida(s)</p>
      </div>

      {filteredNotes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center">
          <Receipt className="mx-auto h-10 w-10 text-gray-300" />
          <h4 className="mt-3 font-semibold text-gray-900">Nenhuma NFS-e encontrada</h4>
          <p className="mt-1 text-sm text-gray-500">Importe um XML/PDF ou ajuste os filtros aplicados.</p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm md:block">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Nota</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Tomador</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Valor</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Arquivos</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Vínculo</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredNotes.map((item) => {
                    const issueDate = safeDate(item.data_emissao);
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-gray-900">NFS-e {item.numero_nfse || 'sem número'}</div>
                          <div className="mt-1 text-xs text-gray-500">{issueDate ? dateFormatter.format(issueDate) : 'Emissão não identificada'}</div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-sm font-medium text-gray-900">{item.tomador_nome || 'Não identificado'}</div>
                          <div className="mt-1 text-xs text-gray-500">{item.tomador_cpf_cnpj || 'CPF/CNPJ não identificado'}</div>
                        </td>
                        <td className="px-5 py-4 text-sm font-semibold text-gray-900">{currencyFormatter.format(Number(item.valor_servico || 0))}</td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1.5">
                            {item.xml_content && <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700"><FileCode2 className="h-3 w-3" /> XML</span>}
                            {item.pdf_base64 && <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700"><FileText className="h-3 w-3" /> PDF</span>}
                            {!item.xml_content && !item.pdf_base64 && <span className="text-xs text-gray-400">Sem arquivo disponível</span>}
                          </div>
                        </td>
                        <td className="px-5 py-4">{renderStatus(item)}</td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openViewer(item)} className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"><Eye className="h-3.5 w-3.5" /> Ver</button>
                            <button onClick={() => openLink(item)} className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"><LinkIcon className="h-3.5 w-3.5" /> {item.invoice_id ? 'Trocar vínculo' : 'Vincular'}</button>
                            <button onClick={() => handleDeleteImportedNfse(item)} className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"><Trash2 className="h-3.5 w-3.5" /> Excluir</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-3 md:hidden">
            {filteredNotes.map((item) => {
              const issueDate = safeDate(item.data_emissao);
              return (
                <article key={item.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">NFS-e {item.numero_nfse || 'sem número'}</h4>
                      <p className="mt-1 text-xs text-gray-500">{issueDate ? dateFormatter.format(issueDate) : 'Emissão não identificada'}</p>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{currencyFormatter.format(Number(item.valor_servico || 0))}</span>
                  </div>
                  <div className="mt-4 border-t border-gray-100 pt-3">
                    <p className="text-sm font-medium text-gray-900">{item.tomador_nome || 'Tomador não identificado'}</p>
                    <p className="mt-1 text-xs text-gray-500">{item.tomador_cpf_cnpj || 'CPF/CNPJ não identificado'}</p>
                  </div>
                  <div className="mt-3">{renderStatus(item)}</div>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <button onClick={() => openViewer(item)} className="inline-flex items-center justify-center gap-1 rounded-lg bg-blue-50 px-2 py-2 text-xs font-semibold text-blue-700"><Eye className="h-3.5 w-3.5" /> Ver</button>
                    <button onClick={() => openLink(item)} className="inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-50 px-2 py-2 text-xs font-semibold text-indigo-700"><LinkIcon className="h-3.5 w-3.5" /> {item.invoice_id ? 'Trocar' : 'Vincular'}</button>
                    <button onClick={() => handleDeleteImportedNfse(item)} className="inline-flex items-center justify-center gap-1 rounded-lg bg-red-50 px-2 py-2 text-xs font-semibold text-red-700"><Trash2 className="h-3.5 w-3.5" /> Excluir</button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ImportedNfseWorkspace;
